import { ObjectUtils } from '@colonist/utils';
import { AudioManager, AudioType } from '@colonist/audio';
import { DiceSettingEnum, GameSpeedSettings, LobbyImage } from '@colonist/types';
import { notificationController, socket } from './EntryShared.ts';
import { getNestedTranslation } from '@colonist/i18n';
import { MapDataController } from '../../../shared/map_data/MapDataController.ts';
import { GameModeDataController } from '@colonist/shared-types';
import { keyBy } from 'lodash-es';
import { PlayerColor } from '@colonist/game-states';
import { UIImagePathController } from './UIEnums.ts';
export class RoomStateChangerNotifier {
    static run(oldRoomState, newRoomState) {
        if (oldRoomState == undefined) return;
        if (oldRoomState.gameSetting.id != newRoomState.gameSetting.id) return;
        const notifier = new RoomStateChangerNotifier(oldRoomState, newRoomState);
        notifier.notify();
    }
    notify() {
        this.playSounds();
        this.notifyPrivateGameChanged();
        this.notifyNoTrollsChanged();
        this.notifyBotAdded();
        this.notifyMapChanged();
        this.notifyGameModeChanged();
        this.notifyDiceTypeChanged();
        this.notifyVpToWinChanged();
        this.notifyCardDiscardLimitChanged();
        this.notifyFriendlyRobberChanged();
        this.notifyHideBankCardsChanged();
        this.notifyMaxPlayersChanged();
        this.notifyGameSpeedChanged();
        this.notifyPlayOrderSelectionActiveChanged();
        this.notifyPlayerDisconnected();
        this.notifyPlayerConnected();
        this.notifyHostChanged();
        this.notifyPlayerColorChanged();
    }
    playSounds() {
        const settingsChanged = !ObjectUtils.isObjectEqual(this.oldRoomState.gameSetting, this.newRoomState.gameSetting);
        if (settingsChanged) {
            AudioManager.play(AudioType.RoomSettingUpdated);
            return;
        }
        const previouslyReadyPlayers = this.oldRoomState.getSessions().filter((session)=>session.isReadyToPlay).length;
        const nowReadyPlayers = this.newRoomState.getSessions().filter((session)=>session.isReadyToPlay).length;
        if (nowReadyPlayers > previouslyReadyPlayers) {
            AudioManager.play(AudioType.RoomGetReady);
            return;
        }
    }
    notifyNoTrollsChanged() {
        if (this.oldRoomState.isNoTrollsGame() == this.newRoomState.isNoTrollsGame()) return;
        if (this.wasAndIsHost) return;
        if (this.newRoomState.isNoTrollsGame()) {
            this.showNotification({
                key: 'strings:roomPage.notifications.enabledNoTrollsGame',
                options: {
                    minimumKarma: this.newRoomState.gameSetting.minimumKarma
                }
            });
        } else {
            this.showNotification({
                key: 'strings:roomPage.notifications.disabledNoTrollsGame'
            });
        }
    }
    notifyPrivateGameChanged() {
        if (this.oldRoomState.gameSetting.privateGame == this.newRoomState.gameSetting.privateGame) return;
        if (this.wasAndIsHost) return;
        if (this.newRoomState.gameSetting.privateGame) {
            this.showNotification({
                key: 'strings:roomPage.notifications.roomChangedToPrivate'
            });
        } else {
            this.showNotification({
                key: 'strings:roomPage.notifications.roomChangedToPublic'
            });
        }
    }
    notifyBotAdded() {
        const oldBotCount = this.oldRoomState.getSessions().filter((session)=>session.isBot).length;
        const newBotCount = this.newRoomState.getSessions().filter((session)=>session.isBot).length;
        if (oldBotCount >= newBotCount) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.botAdded'
        });
    }
    notifyMapChanged() {
        if (this.oldRoomState.gameSetting.mapSetting == this.newRoomState.gameSetting.mapSetting) return;
        if (this.wasAndIsHost) return;
        const mapData = MapDataController.getMapDataForMap(this.newRoomState.gameSetting.mapSetting);
        if (mapData == undefined) return;
        const nestedTranslation = getNestedTranslation({
            key: 'strings:roomPage.notifications.changedMap'
        }, {
            map: mapData.name
        });
        this.showNotification(nestedTranslation);
    }
    notifyGameModeChanged() {
        if (this.oldRoomState.gameSetting.modeSetting == this.newRoomState.gameSetting.modeSetting) return;
        if (this.wasAndIsHost) return;
        const gameModeData = GameModeDataController.getModeDataForMode(this.newRoomState.gameSetting.modeSetting);
        if (gameModeData == undefined) return;
        const nestedTranslation = getNestedTranslation({
            key: 'strings:roomPage.notifications.changedGameMode'
        }, {
            gameMode: gameModeData.name
        });
        this.showNotification(nestedTranslation);
    }
    notifyDiceTypeChanged() {
        if (this.oldRoomState.gameSetting.diceSetting == this.newRoomState.gameSetting.diceSetting) return;
        if (this.wasAndIsHost) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.changedDice',
            options: {
                diceSetting: `${DiceSettingEnum[this.newRoomState.gameSetting.diceSetting]}`
            }
        });
    }
    notifyVpToWinChanged() {
        if (this.oldRoomState.gameSetting.victoryPointsToWin == this.newRoomState.gameSetting.victoryPointsToWin) return;
        if (this.wasAndIsHost) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.changedVpToWin',
            options: {
                vpToWin: this.newRoomState.gameSetting.victoryPointsToWin
            }
        });
    }
    notifyCardDiscardLimitChanged() {
        if (this.oldRoomState.gameSetting.cardDiscardLimit == this.newRoomState.gameSetting.cardDiscardLimit) return;
        if (this.wasAndIsHost) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.changedCardDiscardLimit',
            options: {
                cardDiscardLimit: this.newRoomState.gameSetting.cardDiscardLimit
            }
        });
    }
    notifyFriendlyRobberChanged() {
        if (this.oldRoomState.gameSetting.friendlyRobber == this.newRoomState.gameSetting.friendlyRobber) return;
        if (this.wasAndIsHost) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.changedFriendlyRobber',
            options: {
                context: this.newRoomState.gameSetting.friendlyRobber.toString()
            }
        });
    }
    notifyHideBankCardsChanged() {
        if (this.oldRoomState.gameSetting.hideBankCards == this.newRoomState.gameSetting.hideBankCards) return;
        if (this.wasAndIsHost) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.changedHideBankCards',
            options: {
                context: this.newRoomState.gameSetting.hideBankCards.toString()
            }
        });
    }
    notifyMaxPlayersChanged() {
        if (this.oldRoomState.gameSetting.maxPlayers == this.newRoomState.gameSetting.maxPlayers) return;
        if (this.wasAndIsHost) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.changedMaxPlayers',
            options: {
                maxPlayers: this.newRoomState.gameSetting.maxPlayers
            }
        });
    }
    notifyGameSpeedChanged() {
        if (this.oldRoomState.gameSetting.gameSpeed == this.newRoomState.gameSetting.gameSpeed) return;
        if (this.wasAndIsHost) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.changedTurnTimer',
            options: {
                turnTimer: `${GameSpeedSettings[this.newRoomState.gameSetting.gameSpeed]}`
            }
        });
    }
    notifyPlayOrderSelectionActiveChanged() {
        if (this.oldRoomState.gameSetting.playOrderSelectionActive == this.newRoomState.gameSetting.playOrderSelectionActive) return;
        if (this.wasAndIsHost) return;
        if (this.newRoomState.gameSetting.playOrderSelectionActive) {
            this.showNotification({
                key: 'strings:roomPage.notifications.enabledPlayOrderSelection'
            });
        } else {
            this.showNotification({
                key: 'strings:roomPage.notifications.disabledPlayOrderSelection'
            });
        }
    }
    notifyHostChanged() {
        if (this.oldRoomState.hostSession.roomSessionId == this.newRoomState.hostSession.roomSessionId) return;
        this.showNotification({
            key: 'strings:roomPage.notifications.newHostIs',
            options: {
                username: this.newRoomState.hostSession.username
            }
        });
    }
    notifyPlayerDisconnected() {
        const newSessionsIndexed = keyBy(this.newRoomState.getSessions(), 'roomSessionId');
        for (const oldSession of this.oldRoomState.getSessions()){
            if (newSessionsIndexed[oldSession.roomSessionId] != undefined) continue;
            if (this.oldRoomState.hostSession.roomSessionId == oldSession.roomSessionId) {
                this.showNotification({
                    key: 'strings:roomPage.notifications.hostDisconnected',
                    options: {
                        username: oldSession.username
                    }
                });
            } else if (oldSession.userId != undefined && this.newRoomState.kickedUserIds.includes(oldSession.userId)) {
                this.showNotification({
                    key: 'strings:roomPage.notifications.hostKickedUser',
                    options: {
                        hostUsername: this.newRoomState.hostSession.username,
                        kickedUsername: oldSession.username
                    }
                });
            } else {
                this.showNotification({
                    key: 'strings:roomPage.notifications.userDisconnected',
                    options: {
                        username: oldSession.username
                    }
                });
            }
        }
    }
    notifyPlayerConnected() {
        const oldSessionsIndexed = keyBy(this.oldRoomState.getSessions(), 'roomSessionId');
        for (const session of this.newRoomState.getSessions()){
            if (oldSessionsIndexed[session.roomSessionId] != undefined) continue;
            if (this.mySessionId == session.userSessionId) continue;
            if (session.isBot) continue;
            this.showNotification({
                key: 'strings:roomPage.notifications.userConnected',
                options: {
                    username: session.username
                }
            });
        }
    }
    notifyPlayerColorChanged() {
        const oldSessionsIndexed = keyBy(this.oldRoomState.getSessions(), 'roomSessionId');
        for (const session of this.newRoomState.getSessions()){
            if (session.isBot) continue;
            const oldSession = oldSessionsIndexed[session.roomSessionId];
            if (oldSession == undefined) continue;
            if (oldSession.selectedColor == session.selectedColor) continue;
            this.showNotification({
                key: 'strings:roomPage.notifications.userSelectedColor',
                options: {
                    username: session.username,
                    color: `${PlayerColor[session.selectedColor]}`
                }
            }, UIImagePathController.getPlayerColorBuildings(session.selectedColor).settlement);
        }
    }
    // eslint-disable-next-line class-methods-use-this
    showNotification(text, icon = LobbyImage.Lightning) {
        notificationController.showNotification(icon, text);
    }
    constructor(oldRoomState, newRoomState){
        this.oldRoomState = oldRoomState;
        this.newRoomState = newRoomState;
        this.mySessionId = socket.controller.session.id // TODO better
        ;
        this.wasAndIsHost = oldRoomState.isHost(this.mySessionId) && newRoomState.isHost(this.mySessionId);
    }
}
